import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import LoginPromptModal from '../components/LoginPromptModal';

const PRIMARY = '#2DBCAF';

interface Activity {
  _id: string;
  title: string;
  description: string;
  category: string;
  ageGroup: string;
  image?: string;
  metadata: {
    week: number;
    day: number;
    tips?: string;
  };
}

interface Progress {
  contentId: string;
  status: 'completed' | 'pending' | 'skipped';
}

const ASPECTS = [
  { key: 'motorikKasar', name: 'Mot. Kasar', icon: 'directions_run', color: '#F59E0B' },
  { key: 'motorikHalus', name: 'Mot. Halus', icon: 'front_hand', color: '#10B981' },
  { key: 'bicara', name: 'Bicara', icon: 'record_voice_over', color: '#3B82F6' },
  { key: 'sosial', name: 'Sosial', icon: 'groups', color: '#8B5CF6' },
];

const DAYS = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

type ViewMode = 'weekly' | 'daily';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 100px;
`;

const Header = styled.header`
  background: white;
  padding: 16px 16px 14px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #64748b;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

const ChildTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(45,188,175,0.1);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${PRIMARY};
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
  margin-bottom: 12px;
`;

const ViewBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${p => p.$active ? 'white' : 'transparent'};
  color: ${p => p.$active ? PRIMARY : '#64748b'};
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  .material-symbols-outlined { font-size: 18px; }
`;

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const WeekBtn = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #64748b;
  border-radius: 8px;
  &:hover { background: #f1f5f9; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
  .material-symbols-outlined { font-size: 20px; }
`;

const WeekLabel = styled.div`
  text-align: center;
  h3 { font-size: 14px; font-weight: 700; color: #0f172a; margin: 0; }
  p { font-size: 11px; color: #64748b; margin: 2px 0 0; }
`;

const ProgressBar = styled.div`
  margin: 16px 16px 12px;
  background: white;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${PRIMARY};
  border-radius: 4px;
  transition: width 0.3s;
`;

const ProgressText = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  min-width: 50px;
  text-align: right;
`;

const GridWrapper = styled.div`
  padding: 0 12px;
  overflow-x: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 70px repeat(4, 1fr);
  gap: 6px;
  min-width: 480px;
`;

const AspectHeader = styled.div<{ $color: string }>`
  background: ${p => p.$color};
  color: white;
  padding: 8px 4px;
  border-radius: 10px;
  text-align: center;
  .material-symbols-outlined { font-size: 20px; display: block; margin: 0 auto 2px; }
  span:last-child { font-size: 9px; font-weight: 700; display: block; }
`;

const DayLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 8px 4px;
`;

const ActivityCell = styled.button<{ $done?: boolean; $color?: string }>`
  background: ${p => p.$done ? `${p.$color}15` : 'white'};
  border: 1.5px solid ${p => p.$done ? p.$color : '#e2e8f0'};
  border-radius: 10px;
  padding: 8px 6px;
  cursor: pointer;
  text-align: left;
  min-height: 60px;
  transition: all 0.15s;
  position: relative;
  &:hover { border-color: ${p => p.$color || PRIMARY}; }
`;

const CellTitle = styled.p`
  font-size: 10px;
  font-weight: 600;
  color: #334155;
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CellCheck = styled.div<{ $done?: boolean; $color?: string }>`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${p => p.$done ? p.$color : '#e2e8f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 12px; color: white; }
`;

const EmptyCell = styled.div`
  background: #f8fafc;
  border: 1.5px dashed #e2e8f0;
  border-radius: 10px;
  min-height: 60px;
`;

// Daily View Components
const DailyContainer = styled.div`
  padding: 0 16px;
`;

const DailyCard = styled.div<{ $color: string; $done?: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  border: 2px solid ${p => p.$done ? p.$color : '#e2e8f0'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${p => p.$color};
    transform: translateY(-2px);
  }
`;

const DailyCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const DailyCardIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 26px; color: white; }
`;

const DailyCardInfo = styled.div`
  flex: 1;
`;

const DailyCardCategory = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DailyCardTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 4px 0 0;
`;

const DailyCardCheck = styled.div<{ $done?: boolean; $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: ${p => p.$done ? p.$color : '#f1f5f9'};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 20px; color: ${p => p.$done ? 'white' : '#cbd5e1'}; }
`;

const DailyCardImage = styled.div`
  width: 100%;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f8fafc;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DailyCardDesc = styled.p`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DailyEmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
  
  .material-symbols-outlined {
    font-size: 56px;
    color: #cbd5e1;
    display: block;
    margin-bottom: 12px;
  }
  
  h4 {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 4px;
    color: #475569;
  }
  
  p {
    font-size: 13px;
    margin: 0;
  }
`;

const ReferencesSection = styled.div`
  margin: 24px 16px 0;
  padding: 16px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
`;

const ReferencesTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 16px; }
`;

const ReferencesList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.7;
  li { margin-bottom: 2px; }
`;

const Disclaimer = styled.p`
  font-size: 10px;
  color: #94a3b8;
  margin: 10px 0 0;
  font-style: italic;
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
`;

const ModalSheet = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 24px 20px 36px;
  width: 100%;
  max-width: 512px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ModalIcon = styled.div<{ $color?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.$color || PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 24px; color: white; }
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

const ModalClose = styled.button`
  background: #f1f5f9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  .material-symbols-outlined { font-size: 20px; color: #64748b; }
`;

const ModalDesc = styled.p`
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 16px;
`;

const ModalTips = styled.div`
  background: #fef3c7;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  .material-symbols-outlined { font-size: 20px; color: #f59e0b; flex-shrink: 0; }
  p { font-size: 13px; color: #92400e; margin: 0; line-height: 1.5; }
`;

const ModalBtn = styled.button<{ $done?: boolean }>`
  width: 100%;
  height: 52px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${p => p.$done ? '#f1f5f9' : PRIMARY};
  color: ${p => p.$done ? '#64748b' : 'white'};
  border: none;
  .material-symbols-outlined { font-size: 20px; }
`;

function getAgeGroup(birthDate: string): string {
  const birth = new Date(birthDate);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  
  if (months < 4) return '0-3m';
  if (months < 7) return '4-6m';
  if (months < 10) return '7-9m';
  if (months < 13) return '10-12m';
  if (months < 25) return '1-2y';
  if (months < 37) return '2-3y';
  if (months < 49) return '3-4y';
  if (months < 61) return '4-5y';
  return '5-6y';
}

function getAgeGroupLabel(ageGroup: string): string {
  const map: Record<string, string> = {
    '0-3m': '0-3 Bulan', '4-6m': '4-6 Bulan', '7-9m': '7-9 Bulan', '10-12m': '10-12 Bulan',
    '1-2y': '1-2 Tahun', '2-3y': '2-3 Tahun', '3-4y': '3-4 Tahun', '4-5y': '4-5 Tahun', '5-6y': '5-6 Tahun',
  };
  return map[ageGroup] || ageGroup;
}

export default function StimulasiPage() {
  const navigate = useNavigate();
  const { activeChild, user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [currentWeek, setCurrentWeek] = useState(1);
  const [currentDay, setCurrentDay] = useState(new Date().getDay() || 7); // 1=Mon, 7=Sun
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);

  const ageGroup = activeChild ? getAgeGroup(activeChild.birthDate) : '0-3m';

  // Fetch activities
  useEffect(() => {
    setLoading(true);
    fetch(`/api/tk/content/stimulasi?ageGroup=${ageGroup}&week=${currentWeek}`)
      .then(res => res.json())
      .then(data => {
        if (data.ok) setActivities(data.content);
      })
      .finally(() => setLoading(false));
  }, [ageGroup, currentWeek]);

  // Fetch progress
  useEffect(() => {
    if (!user) return;
    fetch('/api/tk/progress/stimulasi', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.ok) {
          const map: Record<string, boolean> = {};
          data.progress.forEach((p: Progress) => {
            const id = typeof p.contentId === 'string' ? p.contentId : (p.contentId as any)?._id;
            if (p.status === 'completed' && id) map[id] = true;
          });
          setProgress(map);
        }
      });
  }, [user, currentWeek]);

  const grid = useMemo(() => {
    const result: Record<number, Record<string, Activity | null>> = {};
    for (let d = 1; d <= 7; d++) {
      result[d] = {};
      ASPECTS.forEach(a => { result[d][a.key] = null; });
    }
    activities.forEach(act => {
      const day = act.metadata?.day || 1;
      if (result[day]) result[day][act.category] = act;
    });
    return result;
  }, [activities]);

  const stats = useMemo(() => {
    const total = activities.length;
    const done = activities.filter(a => progress[a._id]).length;
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  }, [activities, progress]);

  // Daily activities for current day
  const dailyActivities = useMemo(() => {
    return activities.filter(a => a.metadata?.day === currentDay);
  }, [activities, currentDay]);

  const dailyStats = useMemo(() => {
    const total = dailyActivities.length;
    const done = dailyActivities.filter(a => progress[a._id]).length;
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  }, [dailyActivities, progress]);

  const handleComplete = async (activity: Activity) => {
    const isDone = progress[activity._id];
    try {
      if (isDone) {
        const res = await fetch(`/api/tk/progress/${activity._id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const data = await res.json();
        if (data.error === 'noChild') {
          alert('Tambahkan data anak terlebih dahulu di halaman Profil');
          return;
        }
        setProgress(p => { const n = { ...p }; delete n[activity._id]; return n; });
      } else {
        const res = await fetch('/api/tk/progress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ contentId: activity._id, status: 'completed' }),
        });
        const data = await res.json();
        if (data.error === 'noChild') {
          alert('Tambahkan data anak terlebih dahulu di halaman Profil');
          return;
        }
        if (!data.ok) {
          alert(data.message || 'Gagal menyimpan progress');
          return;
        }
        setProgress(p => ({ ...p, [activity._id]: true }));
      }
    } catch {}
    setSelectedActivity(null);
  };

  // Show login modal if not logged in
  if (!user) {
    return (
      <>
        <SEOMeta
          title="Agenda Stimulasi - KinderGame.id"
          description="Jadwal aktivitas stimulasi harian untuk tumbuh kembang anak"
          path="/tumbuh-kembang/stimulasi"
        />
        <PageWrapper>
          <Header>
            <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
            <HeaderTitle>
              <h1>Agenda Stimulasi</h1>
              <p>Aktivitas harian untuk si kecil</p>
            </HeaderTitle>
          </Header>
        </PageWrapper>
        <BottomNavBar />
        <LoginPromptModal
          title="Ups! Kamu Belum Login"
          subtitle="Login untuk melihat agenda stimulasi anak"
          onClose={() => navigate('/tumbuh-kembang')}
        />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title="Agenda Stimulasi - KinderGame.id"
        description="Jadwal aktivitas stimulasi harian untuk tumbuh kembang anak"
        path="/tumbuh-kembang/stimulasi"
      />
      <PageWrapper>
        <Header>
          <HeaderTop>
            <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
            <HeaderTitle>Agenda Stimulasi</HeaderTitle>
            {activeChild && (
              <ChildTag>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>face</span>
                {activeChild.name}
              </ChildTag>
            )}
          </HeaderTop>
          
          {/* View Toggle */}
          <ViewToggle>
            <ViewBtn $active={viewMode === 'daily'} onClick={() => setViewMode('daily')}>
              <span className="material-symbols-outlined">today</span>
              Harian
            </ViewBtn>
            <ViewBtn $active={viewMode === 'weekly'} onClick={() => setViewMode('weekly')}>
              <span className="material-symbols-outlined">date_range</span>
              Mingguan
            </ViewBtn>
          </ViewToggle>

          {/* Navigation - Daily or Weekly */}
          {viewMode === 'daily' ? (
            <WeekNav>
              <WeekBtn onClick={() => setCurrentDay(d => Math.max(1, d - 1))} disabled={currentDay <= 1}>
                <span className="material-symbols-outlined">chevron_left</span>
              </WeekBtn>
              <WeekLabel>
                <h3>{DAYS[currentDay - 1]}</h3>
                <p>Minggu {currentWeek} • {getAgeGroupLabel(ageGroup)}</p>
              </WeekLabel>
              <WeekBtn onClick={() => setCurrentDay(d => Math.min(7, d + 1))} disabled={currentDay >= 7}>
                <span className="material-symbols-outlined">chevron_right</span>
              </WeekBtn>
            </WeekNav>
          ) : (
            <WeekNav>
              <WeekBtn onClick={() => setCurrentWeek(w => Math.max(1, w - 1))} disabled={currentWeek <= 1}>
                <span className="material-symbols-outlined">chevron_left</span>
              </WeekBtn>
              <WeekLabel>
                <h3>Minggu {currentWeek}</h3>
                <p>Usia {getAgeGroupLabel(ageGroup)}</p>
              </WeekLabel>
              <WeekBtn onClick={() => setCurrentWeek(w => w + 1)} disabled={currentWeek >= 4}>
                <span className="material-symbols-outlined">chevron_right</span>
              </WeekBtn>
            </WeekNav>
          )}
        </Header>

        <ProgressBar>
          <ProgressTrack>
            <ProgressFill $pct={viewMode === 'daily' ? dailyStats.pct : stats.pct} />
          </ProgressTrack>
          <ProgressText>
            {viewMode === 'daily' ? `${dailyStats.done}/${dailyStats.total}` : `${stats.done}/${stats.total}`}
          </ProgressText>
        </ProgressBar>

        {/* Daily View */}
        {viewMode === 'daily' && (
          <DailyContainer>
            {dailyActivities.length === 0 && !loading ? (
              <DailyEmptyState>
                <span className="material-symbols-outlined">event_busy</span>
                <h4>Tidak Ada Aktivitas</h4>
                <p>Belum ada aktivitas stimulasi untuk hari {DAYS[currentDay - 1]}</p>
              </DailyEmptyState>
            ) : (
              dailyActivities.map(act => {
                const aspect = ASPECTS.find(a => a.key === act.category);
                const done = progress[act._id];
                return (
                  <DailyCard 
                    key={act._id} 
                    $color={aspect?.color || PRIMARY}
                    $done={done}
                    onClick={() => setSelectedActivity(act)}
                  >
                    <DailyCardHeader>
                      <DailyCardIcon $color={aspect?.color || PRIMARY}>
                        <span className="material-symbols-outlined">{aspect?.icon || 'star'}</span>
                      </DailyCardIcon>
                      <DailyCardInfo>
                        <DailyCardCategory $color={aspect?.color || PRIMARY}>
                          {aspect?.name || act.category}
                        </DailyCardCategory>
                        <DailyCardTitle>{act.title}</DailyCardTitle>
                      </DailyCardInfo>
                      <DailyCardCheck $done={done} $color={aspect?.color || PRIMARY}>
                        <span className="material-symbols-outlined">
                          {done ? 'check' : 'radio_button_unchecked'}
                        </span>
                      </DailyCardCheck>
                    </DailyCardHeader>
                    {act.image && (
                      <DailyCardImage>
                        <img src={act.image} alt={act.title} />
                      </DailyCardImage>
                    )}
                    <DailyCardDesc>{act.description || 'Tidak ada deskripsi.'}</DailyCardDesc>
                  </DailyCard>
                );
              })
            )}
          </DailyContainer>
        )}

        {/* Weekly View */}
        {viewMode === 'weekly' && (
          <>
            <GridWrapper>
              <Grid>
                {/* Header row */}
                <div />
                {ASPECTS.map(a => (
                  <AspectHeader key={a.key} $color={a.color}>
                    <span className="material-symbols-outlined">{a.icon}</span>
                    <span>{a.name}</span>
                  </AspectHeader>
                ))}

                {/* Day rows */}
                {DAYS.map((day, idx) => (
                  <React.Fragment key={day}>
                    <DayLabel>{day}</DayLabel>
                    {ASPECTS.map(aspect => {
                      const act = grid[idx + 1]?.[aspect.key];
                      if (!act) return <EmptyCell key={aspect.key} />;
                      const done = progress[act._id];
                      return (
                        <ActivityCell
                          key={aspect.key}
                          $done={done}
                          $color={aspect.color}
                          onClick={() => setSelectedActivity(act)}
                        >
                          <CellTitle>{act.title}</CellTitle>
                          <CellCheck $done={done} $color={aspect.color}>
                            {done && <span className="material-symbols-outlined">check</span>}
                          </CellCheck>
                        </ActivityCell>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Grid>
            </GridWrapper>

            {activities.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: '#64748b' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#cbd5e1' }}>calendar_month</span>
                <p style={{ marginTop: 12 }}>Belum ada aktivitas untuk minggu ini</p>
              </div>
            )}
          </>
        )}

        {/* References */}
        <ReferencesSection>
          <ReferencesTitle>
            <span className="material-symbols-outlined">menu_book</span>
            Referensi Konten
          </ReferencesTitle>
          <ReferencesList>
            <li>CDC Developmental Milestones (Centers for Disease Control and Prevention)</li>
            <li>WHO Child Growth Standards (World Health Organization)</li>
            <li>KPSP - Kuesioner Pra Skrining Perkembangan (Kemenkes RI)</li>
            <li>Pedoman Stimulasi, Deteksi, dan Intervensi Dini Tumbuh Kembang Anak</li>
            <li>Kurikulum Pendidikan Anak Usia Dini (PAUD) Indonesia</li>
          </ReferencesList>
          <Disclaimer>
            * Konten ini bersifat panduan umum. Untuk evaluasi tumbuh kembang anak, konsultasikan dengan dokter anak atau ahli tumbuh kembang.
          </Disclaimer>
        </ReferencesSection>
      </PageWrapper>

      <BottomNavBar />

      {/* Activity Detail Modal */}
      {selectedActivity && (
        <ModalOverlay onClick={() => setSelectedActivity(null)}>
          <ModalSheet onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalIcon $color={ASPECTS.find(a => a.key === selectedActivity.category)?.color}>
                <span className="material-symbols-outlined">
                  {ASPECTS.find(a => a.key === selectedActivity.category)?.icon}
                </span>
              </ModalIcon>
              <ModalTitle>{selectedActivity.title}</ModalTitle>
              <ModalClose onClick={() => setSelectedActivity(null)}>
                <span className="material-symbols-outlined">close</span>
              </ModalClose>
            </ModalHeader>

            {selectedActivity.image && (
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <img 
                  src={selectedActivity.image} 
                  alt={selectedActivity.title}
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    objectFit: 'contain',
                    borderRadius: '12px',
                    background: '#f1f5f9'
                  }}
                />
              </div>
            )}

            <ModalDesc>{selectedActivity.description || 'Tidak ada deskripsi.'}</ModalDesc>

            {selectedActivity.metadata?.tips && (
              <ModalTips>
                <span className="material-symbols-outlined">lightbulb</span>
                <p>{selectedActivity.metadata.tips}</p>
              </ModalTips>
            )}

            <ModalBtn
              $done={progress[selectedActivity._id]}
              onClick={() => handleComplete(selectedActivity)}
            >
              {progress[selectedActivity._id] ? (
                <>
                  <span className="material-symbols-outlined">undo</span>
                  Batalkan
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">check_circle</span>
                  Tandai Selesai
                </>
              )}
            </ModalBtn>
          </ModalSheet>
        </ModalOverlay>
      )}
    </>
  );
}
